import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Clock, Calendar, X } from 'lucide-react';
import TimeRangeSlider from './TimeRangeSlider';

const AvailabilityHeader = ({ 
  activeFilter, 
  handleFilterClick, 
  showTimeFilter, 
  handleTimeChange, 
  setShowTimeFilter,
  selectedTimeRange,
  setSelectedTimeRange,
  setActiveFilter 
}) => {
  const formatTimeDisplay = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const FilterButton = ({ label, active, onClick, children }) => {
    return (
      <button
        onClick={onClick}
        className={`
          transition-all duration-300 font-medium
          px-6 py-2.5 rounded-full text-sm border
          flex items-center gap-2
          ${active ? 
            'bg-white text-black border-white' : 
            'bg-transparent text-gray-400 border-gray-800 hover:border-gray-600 hover:text-white'
          }
        `}
      >
        {children}
        {label}
      </button>
    );
  };

  return (
    <div className="px-4 pb-8 md:pb-12">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Find an Empty Classroom
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-lg">
            Check real-time availability and schedule for all campus rooms
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center items-center gap-3"
        >
          <FilterButton 
            label="All Buildings" 
            active={activeFilter === 'all'}
            onClick={() => handleFilterClick('all')}
          >
            <Building2 className="w-4 h-4" />
          </FilterButton>
          
          <FilterButton 
            label="Available Now" 
            active={activeFilter === 'available'}
            onClick={() => handleFilterClick('available')}
          >
            <Clock className="w-4 h-4" />
          </FilterButton>

          <div className="relative">
            <FilterButton 
              label="Select Time" 
              active={activeFilter === 'time'}
              onClick={() => handleFilterClick('time')}
            >
              <Calendar className="w-4 h-4" />
            </FilterButton>
            
            {showTimeFilter && (
              <TimeRangeSlider 
                onTimeChange={handleTimeChange}
                onClose={() => setShowTimeFilter(false)}
              />
            )}
          </div>

          {selectedTimeRange && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-4 py-2.5 bg-purple-500/10 text-purple-300 rounded-full text-sm border border-purple-500/20 flex items-center gap-2"
            >
              <span>
                {`${formatTimeDisplay(selectedTimeRange.start)} - ${formatTimeDisplay(selectedTimeRange.end)}`}
              </span>
              <button
                className="hover:text-white transition-colors ml-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTimeRange(null);
                  setActiveFilter('all');
                  localStorage.removeItem('selectedTimeRange');
                }}
              >
                <X size={14} />
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AvailabilityHeader;